<template>
  <div>
    <b-sidebar
      ref="sb"
      v-model="state"
      v-resize="onResize"
      :width="`${width}px`"
      :no-header-close="true"
      right
      shadow
      no-close-on-esc
      no-close-on-backdrop
    >
      <div v-if="state" class="bg-white px-1 px-lg-3">
        <b-tabs
          v-model="activeTab"
          pills
          :card="false"
          nav-wrapper-class="py-2"
          content-class="px-0"
          @activate-tab="onShowTab"
        >
          <b-tab
            :title="`${$t('fCustomer_invt_receivedTitle')} (${info.invites.received})`"
            active
          >
            <data-layout
              v-if="userId && activeTab === 0"
              ref="dtReceived"
              :fields="receivedFields"
              :filter="filterReceived"
              :repo="repo"
              :parent-id="userId"
              :height="height"
              :limit="50"
              card-collapse
              @on-sort="onSort"
            >
              <template #card(header)="{ data: { item }}">
                <user-profile-link v-if="item" :user="item.user" />
                <div v-if="item" class="small">
                  <span class="text-muted">{{ $dateFormat(item.created_at) }}</span>
                </div>
              </template>
              <template #cell(user_name)="{ data }">
                <user-profile-item
                  :user="data.item.user"
                />
              </template>
            </data-layout>
          </b-tab>
          <b-tab
            :title="`${$t('fCustomer_invt_sentTitle')} (${info.invites.sent})`"
          >
            <data-layout
              v-if="userId && activeTab === 1"
              ref="dtSent"
              :fields="sentFields"
              :filter="filterSent"
              :repo="repo"
              :parent-id="userId"
              :height="height"
              :limit="50"
              card-collapse
              @on-sort="onSort"
            >
              <template #card(header)="{ data: { item }}">
                <user-profile-link v-if="item" :user="item.invitee" />
                <div v-if="item" class="small">
                  <span class="text-muted">{{ $dateFormat(item.created_at) }}</span>
                </div>
              </template>
              <template #cell(invitee_name)="{ data }">
                <user-profile-item
                  :user="data.item.invitee"
                />
              </template>
            </data-layout>
          </b-tab>
        </b-tabs>
      </div>
      <template #header>
        <b-link class="mr-2 text-info" @click="onCancel">
          <font-awesome-icon v-if="isMobile" :icon="['fas', 'arrow-left']" class="mr-2" />
          <span v-shtml="parentTitle" />
        </b-link>
        - {{ displayTitle }}
      </template>
      <template #footer>
        <div v-if="!isMobile" class="d-flex bg-light align-items-center px-3 py-2">
          <strong class="mr-auto" />
          <b-btn variant="secondary" size="sm" @click="onCancel">
            {{ $t('eDoc_form_a_close') }}
          </b-btn>
        </div>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { isServer } from '@/mixins/helpers'

export default {
  name: 'InvitationHistory',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    userId: {
      type: [String, Number],
      required: true
    },
    idx: {
      type: Number,
      default: -1
    },
    info: {
      type: Object,
      default: () => ({})
    },
    parentTitle: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      repo: 'customers.invites',
      receivedFields: [
        {
          key: 'user_name',
          label: this.$t('fCustomer_invt_f_nick'),
          sortable: true,
          cardFullWidth: true
        },
        {
          key: 'codice',
          label: this.$t('fCustomer_invt_f_codice'),
          sortable: true
        },
        {
          key: 'user_email',
          label: this.$t('fCustomer_invt_f_email'),
          sortable: true,
          formatter: (value, key, item) => {
            return item.user &&
              item.user.email
          }
        },
        {
          key: 'created_at',
          label: this.$t('fCustomer_invt_f_date'),
          sortable: true,
          formatter: (value, key, item) => {
            return this.$dateFormat(value)
          }
        }
      ],
      sentFields: [
        {
          key: 'invitee_name',
          label: this.$t('fCustomer_invt_f_nick'),
          sortable: true,
          cardFullWidth: true
        },
        {
          key: 'invitee_email',
          label: this.$t('fCustomer_invt_f_email'),
          sortable: true,
          formatter: (value, key, item) => {
            return item.invitee &&
              item.invitee.email
          }
        },
        {
          key: 'created_at',
          label: this.$t('fCustomer_invt_f_date'),
          sortable: true,
          formatter: (value, key, item) => {
            return this.$dateFormat(value)
          }
        }
      ],
      filter: [],
      state: this.value,
      activeTab: null,
      busy: false,
      height: 500,
      width: 300
    }
  },
  async fetch () {
    if (this.$refs.dtReceive && this.activeTab === 0) {
      await this.$refs.dtReceived.$fetch()
    } else if (this.$refs.dtSent && this.activeTab === 1) {
      await this.$refs.dtSent.$fetch()
    }
  },
  computed: {
    ...mapGetters({
      deviceInfo: 'device'
    }),
    isMobile () {
      return this.deviceInfo?.type === 'mobile' || false
    },
    displayTitle () {
      return `${this.$t('fCustomer_invt_title')} (${this.total})`
    },
    filterReceived () {
      return [
        { column: 'invitations.invitee_id', value: this.userId, permanent: true }
      ].concat(this.filter)
    },
    filterSent () {
      return [
        { column: 'invitations.user_id', value: this.userId, permanent: true }
      ].concat(this.filter)
    },
    total () {
      return this.info.invites.tot || 0
    }
  },
  watch: {
    value (n) {
      this.state = n
      if (!n) {
        this.activeTab = null
        this.flushData()
      }
      this.$nextTick(() => {
        this.calcHeight()
        this.calcWidth()
      })
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.calcHeight()
      this.calcWidth()
    })
    if (window) {
      window.addEventListener('resize', this.calcHeight)
    }
  },
  beforeDestroy () {
    if (window) {
      window.removeEventListener('resize', this.calcHeight)
    }
  },
  methods: {
    ...mapActions({
      call: 'repos/call',
      flush: 'repos/flush',
      flushError: 'repos/flushError'
    }),
    onSort (order) {
      this.order = order
    },
    flushData () {
      this.flush({
        repo: this.repo,
        method: 'index',
        id: this.itemId,
        idx: this.idx
      })
    },
    onCancel () {
      this.$emit('input', false)
    },
    onShowTab (activeTab) {
      this.flushData()
      this.$forceUpdate()
      this.$nextTick(() => {
        this.$fetch()
      })
    },
    calcHeight () {
      if (!isServer && window) {
        const $sidebar = this.$refs.sb?.$el?.querySelector('.b-sidebar-body')
        const $pager = this.$refs.sb?.$el?.querySelector('.pager')
        const $tabsNav = this.$refs.sb?.$el?.querySelector('.b-sidebar-body .tabs .nav')?.parentElement
        const sidebarHeight = $sidebar?.clientHeight || 0
        const pagerHeight = $pager?.clientHeight || 0
        const tabsNavHeight = $tabsNav?.clientHeight || 0
        // calc.
        this.height = sidebarHeight - pagerHeight - tabsNavHeight
      }
    },
    calcWidth () {
      let width = 0
      if (!isServer) {
        if (this.deviceInfo?.type === 'mobile') {
          width = document.body.offsetWidth
        } else {
          width = Math.round(document.body.offsetWidth * 0.80) - (15 * (this.idx + 1))
        }
      }
      this.width = width
    },
    onResize () {
      this.calcHeight()
      this.calcWidth()
    }
  }
}
</script>

<style scoped>

</style>
